import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	MillSetupSheetData,
	ProductHeader,
	ProductSizeOption,
	ProductTypeOption,
	RollsByStandRow,
	StandMaster,
	StandSetupRow,
} from "../models/MillSetupSheet";

const round3 = (value: number | null | undefined): number | null =>
	value == null ? null : parseFloat(Number(value).toFixed(3));

export interface MillSetupSheetResponse {
	header: ProductHeader | null;
	sheet: MillSetupSheetData | null;
}

export const getProductTypes = async (): Promise<ProductTypeOption[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "OnlyActives", value: true }];

	const resp = await apiService.callV2("[MILL].[GetProductTypes]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as ProductTypeOption[];
	}
	throw new Error(resp.message || "Error fetching product types");
};

export const getProductSizes = async (
	productTypeId: number | null,
): Promise<ProductSizeOption[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "ProductTypeId", value: productTypeId },
		{ name: "OnlyActives", value: true },
	];

	const resp = await apiService.callV2("[MILL].[GetProductSizes]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as ProductSizeOption[];
	}
	throw new Error(resp.message || "Error fetching product sizes");
};

export const getCommonStands = async (): Promise<StandMaster[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "StandType", value: "COMMON" },
		{ name: "OnlyActives", value: true },
	];

	const resp = await apiService.callV2("[MILL].[GetStands]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as StandMaster[];
	}
	throw new Error(resp.message || "Error fetching stands");
};

export const getMillSetupSheet = async (
	productSizeId: number,
): Promise<MillSetupSheetResponse> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "productSizeId", value: productSizeId },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetMillSetupSheet]",
		parameters,
	);

	if (resp.ok) {
		return {
			header: get(resp, "data.tables[0].rows[0]", null) as ProductHeader | null,
			sheet: get(
				resp,
				"data.tables[1].rows[0]",
				null,
			) as MillSetupSheetData | null,
		};
	}
	throw new Error(resp.message || "Error fetching mill setup sheet");
};

export const getProductMillSetup = async (
	productSizeId: number,
): Promise<StandSetupRow[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "ProductSizeId", value: productSizeId },
		{ name: "ActiveOnly", value: true },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetProductMillSetup]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as StandSetupRow[];
	}
	throw new Error(resp.message || "Error fetching product mill setup");
};

export const getRollsByStand = async (
	productSizeId: number,
): Promise<RollsByStandRow[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "ProductSizeId", value: productSizeId },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetMillSetupSheetRollsByStand]",
		parameters,
	);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as RollsByStandRow[];
		return rows.map((r) => ({ ...r, Diameter: round3(r.Diameter) }));
	}
	throw new Error(resp.message || "Error fetching roll diameters by stand");
};

export const upsertMillSetupSheet = async (params: {
	productSizeId: number;
	specialComments: string;
	comments: string;
}): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "productSizeId", value: params.productSizeId },
		{ name: "specialComments", value: params.specialComments },
		{ name: "comments", value: params.comments },
	];

	const resp = await apiService.callV2(
		"[MILL].[UpsertMillSetupSheet]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error saving mill setup sheet");
	}
};
